import React, { Suspense, useEffect, useState } from 'react';
import { CustomTable } from '../components/CustomTable';
import { logsTableHeaders } from '../utils/Enums/TableHeaders';
import { PaginationComponent } from '../components/Pagination';
import { Filter, TableComponentProps, TableInnerProps } from '../models/table-models';
import { resourceCache } from 'use-async-resource';
import { useTranslation } from 'react-i18next';
import { NoDataComponent } from '../components/NoDataComponent';
import { Log } from '../models/log-models';
import { useGetAllLogs, useGetLogOperations } from '../utils/logger-hooks';
import { useTableData } from '../utils/data-hooks';
import { transformDateToString } from '../utils/helper-functions';
import { ErrorComponent } from '../components/ErrorComponent';
import { Spinner } from 'react-bootstrap';
import ErrorBoundary from '../components/ErrorBoundary';

export const Logs = ({ filter, pagination }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const [newFilter, setFilter] = useState<Filter>({ tags: [], search: '' });
    const [getLogs, fetchNewLogs] = useTableData<Log[]>(useGetAllLogs, pagination, filter);

    useEffect(() => {
        setFilter(filter);
    }, [filter]);

    useEffect(() => {
        resourceCache(useGetLogOperations).clear();
        resourceCache(useGetAllLogs).clear();
    }, []);

    const refetchLogsHook = async () => {
        resourceCache(useGetAllLogs).clear();
        fetchNewLogs({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter: newFilter,
        });
    };

    return (
        <>
            <ErrorBoundary fallback={<ErrorComponent errorText={t('logs')} />}>
                <Suspense fallback={<Spinner animation='border' />}>
                    <LogsInner
                        pagination={pagination}
                        getEntities={getLogs}
                        refetchEntitiesHook={refetchLogsHook}
                    />
                </Suspense>
            </ErrorBoundary>
        </>
    );
};

const LogsInner = ({ getEntities, pagination, refetchEntitiesHook }: TableInnerProps<Log>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const logsTranslation = useTranslation('translation', { keyPrefix: 'logs' });
    const { pager, items } = getEntities();

    return (
        <>
            {pager.totalCount > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            headings: logsTableHeaders,
                            items: items.map(({ userName, createdOn, groupName, log, operation }) => [
                                userName,
                                transformDateToString(createdOn, 'dd/mm/yyyy HH:MM:ss'),
                                groupName,
                                log,
                                logsTranslation.t(operation),
                            ]),
                        }}
                        tableData={items}
                        refetchEntitiesHook={refetchEntitiesHook}
                    />
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                </>
            ) : (
                <NoDataComponent noDataText={t('logs')} />
            )}
        </>
    );
};
